import React from 'react';
import { Box, TextField, InputAdornment, MenuItem, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import IpdTextField from '../../ipd/utils/IpdTextField';
import InvButton from './InvButton';
import { InvTextField } from './InvField';
import { IPD_COLORS, inputSx, FILTER_TOOLBAR, toolbarSx } from '../theme';
import { IPD_FIELD_HEIGHT } from '../../ipd/utils/ipdFieldStyles';

const FILTER_WIDTHS = {
  default: FILTER_TOOLBAR.filterWidth,
  wide: FILTER_TOOLBAR.filterWidthWide,
};

function filterWidthSx(width = 'default') {
  const px = typeof width === 'number' ? width : (FILTER_WIDTHS[width] || FILTER_WIDTHS.default);
  return {
    flex: { xs: '1 1 100%', sm: `0 0 ${px}px` },
    width: { xs: '100%', sm: px },
    minWidth: { xs: '100%', sm: px },
    maxWidth: { xs: '100%', sm: px },
  };
}

/** Unified search + filter bar for list pages */
function FilterToolbar({ children, variant = 'flat', sx = {} }) {
  return (
    <Box
      role="toolbar"
      aria-label="Filters"
      sx={{
        ...toolbarSx,
        ...(variant === 'flat' ? { mb: FILTER_TOOLBAR.mb } : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function FilterToolbarSearch({
  value,
  onChange,
  placeholder = 'Search…',
  sx,
  inputProps,
  ...rest
}) {
  return (
    <Box
      sx={{
        flex: `1 1 ${FILTER_TOOLBAR.searchMin}px`,
        minWidth: { xs: '100%', sm: FILTER_TOOLBAR.searchMin },
        maxWidth: { xs: '100%', sm: FILTER_TOOLBAR.searchMax },
        width: { xs: '100%', sm: 'auto' },
        ...sx,
      }}
    >
      <TextField
        fullWidth
        size="small"
        hiddenLabel
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        sx={inputSx}
        inputProps={{ 'aria-label': placeholder, ...inputProps }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        {...rest}
      />
    </Box>
  );
}

function FilterToolbarFilter({ label, width = 'default', sx, value, onChange, options = [], ...rest }) {
  return (
    <Box sx={{ ...filterWidthSx(width), ...sx }}>
      <IpdTextField
        select
        label={label}
        value={value ?? ''}
        onChange={onChange}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        SelectProps={{ displayEmpty: true }}
        {...rest}
      >
        {options.map(opt => (
          <MenuItem key={opt.value ?? opt} value={opt.value ?? opt}>
            {opt.label ?? opt}
          </MenuItem>
        ))}
      </IpdTextField>
    </Box>
  );
}

/**
 * Read-only stand-in for FilterToolbar.Filter, sized to line up with it.
 *
 * Used where a filter is fixed by the backend rather than chosen by the user — a Store
 * Keeper is scoped to `inventory_details.store_id`, so their store is a fact about the
 * screen, not a choice. Rendered as a label/value pair rather than a disabled select
 * because there is no hidden list of alternatives to imply.
 */
function FilterToolbarStaticValue({ label, value, width = 'default', sx }) {
  return (
    <Box sx={{ ...filterWidthSx(width), alignSelf: 'flex-end', ...sx }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography
        title={value || undefined}
        sx={{
          height: IPD_FIELD_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          fontSize: 14,
          fontWeight: 600,
          color: IPD_COLORS.textPrimary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

/**
 * Date filter that lines up with FilterToolbar.Filter — the shared date field
 * reserves a helper-text row, which would push the input up in a flex-end row.
 */
function FilterToolbarDateFilter({ label, width = 150, sx, value, onChange, ...rest }) {
  return (
    <Box sx={{ ...filterWidthSx(width), ...sx }}>
      <InvTextField
        label={label}
        type="date"
        value={value}
        onChange={onChange}
        fullWidth
        sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
        {...rest}
      />
    </Box>
  );
}

function FilterToolbarReset({ onClick, disabled, label = 'Reset' }) {
  return (
    <InvButton
      variant="outlined"
      tone="teal"
      size="small"
      startIcon={<FilterListOffIcon />}
      onClick={onClick}
      disabled={disabled}
      sx={{
        height: IPD_FIELD_HEIGHT,
        flexShrink: 0,
        alignSelf: 'flex-end',
      }}
    >
      {label}
    </InvButton>
  );
}

function FilterToolbarSpacer() {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 8,
        display: { xs: 'none', md: 'block' },
        alignSelf: 'stretch',
      }}
    />
  );
}

function FilterToolbarActions({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        flexShrink: 0,
        width: { xs: '100%', md: 'auto' },
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Wrapper for custom toolbar content (chips, toggles, switches) */
function FilterToolbarSlot({ children, width, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        flex: '0 0 auto',
        alignSelf: 'flex-end',
        ...(width != null ? filterWidthSx(width) : {}),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

FilterToolbar.Search = FilterToolbarSearch;
FilterToolbar.Filter = FilterToolbarFilter;
FilterToolbar.StaticValue = FilterToolbarStaticValue;
FilterToolbar.DateFilter = FilterToolbarDateFilter;
FilterToolbar.Reset = FilterToolbarReset;
FilterToolbar.Spacer = FilterToolbarSpacer;
FilterToolbar.Actions = FilterToolbarActions;
FilterToolbar.Slot = FilterToolbarSlot;

export default FilterToolbar;
